import * as React from 'react'
import { ReactElement, ReactNode, useEffect, useState } from 'react'
import { Toast, ToastId, ToastIntent, ToastTitle } from '@fluentui/react-components'
import ToastDismiss from './ToastDismiss'

export type PromiseToastRenderers<T> = {
    pending: ReactNode
    success: (value: T) => ReactNode
    error: (error: unknown) => ReactNode
}

type PromiseToastContentProps<T> = PromiseToastRenderers<T> & {
    toastId: ToastId
    promise: Promise<T>
    onSettled: (toastId: ToastId, intent: ToastIntent) => void
}

/**
 * Follows a promise and shows its outcome in a single toast.
 *
 * Fluent cannot replace the content of an existing toast, so the toast is dispatched once with this
 * component inside it and the component re-renders itself as the promise settles. The toast's own
 * intent and timeout are not part of its content, so those are pushed through onSettled.
 */
export default function PromiseToastContent<T>({ toastId, promise, pending, success, error, onSettled }: PromiseToastContentProps<T>): ReactElement {
    const [content, setContent] = useState<ReactNode>(pending)

    useEffect(() => {
        let mounted = true

        promise.then(
            value => {
                if (mounted) {
                    setContent(success(value))
                    onSettled(toastId, 'success')
                }
            },
            reason => {
                if (mounted) {
                    setContent(error(reason))
                    onSettled(toastId, 'error')
                }
            },
        )

        return () => {
            mounted = false
        }
    }, [])

    return (
        <Toast>
            <ToastTitle action={<ToastDismiss />}>{content}</ToastTitle>
        </Toast>
    )
}
